'use client';

import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useRouter } from 'next/navigation';
import { usePrivy, useWallets } from '@privy-io/react-auth';
import { useIsMutating } from '@tanstack/react-query';

import { useAuth } from '@/hooks';
import { clearPersistedReferralCode, getPersistedReferralCode } from '@/hooks';
import { getAccessToken, getPersistedTokenForRefresh } from '@/lib/authToken';
import { clearOAuthContext, getOAuthContext } from '@/lib/oauthContext';
import { authApi, mapBackendUserToUser, useLogout, walletApi } from '@/services';
import { store, useAppDispatch } from '@/store';
import {
  clearAuth,
  clearError,
  setError,
  setLoading,
  setSetupRequested,
  setTokens,
  setUser,
  updateUser,
} from '@/store/slices/auth.slice';
import { deleteWCIDB, restoreWCSessionForUser } from '@/utils/wcSession.utils';

import { MESSAGES } from '@/constants';

// Embedded wallet creation posts to Privy's hidden wallet iframe, which may not
// have finished loading right after a headless (whitelabel) login — the SDK then
// throws synchronously ("iframe not initialized"). Retry with backoff so a
// transient failure doesn't permanently strand the user without a wallet.
const MAX_WALLET_CREATE_ATTEMPTS = 5;
const WALLET_CREATE_RETRY_BASE_MS = 1500;

export function AuthInitializer() {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { accessToken, user } = useAuth();
  const {
    ready,
    authenticated,
    user: privyUser,
    getAccessToken: getPrivyToken,
    createWallet,
  } = usePrivy();
  const { wallets } = useWallets();
  const logoutMutation = useLogout();
  const isLoggingOut = useIsMutating({ mutationKey: ['logout'] }) > 0;
  const refreshAttemptedRef = useRef(false);
  const isSyncingRef = useRef(false);
  const lastInitTokenRef = useRef<string | null>(null);
  // Wallet-creation state is tracked per Privy user id so that a logout →
  // login of a different user in the same tab gets a fresh creation attempt.
  const walletCreationRef = useRef<{
    userId: string | null;
    attempts: number;
    inFlight: boolean;
    done: boolean;
  }>({ userId: null, attempts: 0, inFlight: false, done: false });
  const [walletCreateRetryTick, setWalletCreateRetryTick] = useState(0);
  const hasLinkedNewWalletRef = useRef(false);
  const linkedNewWalletUserIdRef = useRef<string | null>(null);
  const normalizedUserWallet = user?.walletAddress?.toLowerCase();
  const linkedWallet = wallets.find((w) => w.address?.toLowerCase() === normalizedUserWallet);
  const embeddedWallet = wallets.find((w) => w.walletClientType === 'privy');
  // SECURITY: never fall back to wallets[0] because it may point to an unrelated
  // browser-connected external wallet from another session.
  const selectedWallet = linkedWallet || embeddedWallet;
  const selectedWalletAddress = selectedWallet?.address || privyUser?.wallet?.address;
  const selectedWalletType =
    selectedWallet?.walletClientType === 'privy' ? 'privy_embedded' : 'external';

  /**
   * Orchestrates the authentication initialization  flow.
   * 1. Attempts to restore session from storage via refresh token.
   * 2. Synchronizes the user profile from the backend to ensure Redux is up to date.
   */
  const initAuth = async () => {
    // If we are actively logging out or already syncing, suspend
    if (isLoggingOut || isSyncingRef.current) return;

    // ── Cross-user WalletConnect session swap ────────────────────────────────
    // On logout we snapshot each user's WC IDB to localStorage under their Privy ID
    // (via saveWCSessionForUser) and record the outgoing user ID in __pm_last_uid__.
    // Here, once Privy has authenticated the incoming user, we act on the marker:
    //
    //   • Same user  → remove marker, continue. WC IDB is intact → auto-reconnects. ✓
    //   • Different user → swap WC IDB with the incoming user's saved snapshot
    //     (if any), then reload. On reload the WC client reads the correct session. ✓
    //     If no snapshot exists for the incoming user, the IDB is wiped → clean slate.
    if (ready && authenticated && privyUser?.id) {
      const lastUid = localStorage.getItem('__pm_last_uid__');
      if (lastUid !== null) {
        localStorage.removeItem('__pm_last_uid__');
        if (lastUid !== privyUser.id) {
          // Attempt to restore this user's previously-saved WC session.
          // restoreWCSessionForUser deletes the current IDB first, then writes the
          // saved entries (if any). Returns false when there is nothing to restore.
          const restored = await restoreWCSessionForUser(privyUser.id).catch(() => false);
          if (!restored) {
            // No saved session for this user → ensure IDB is clean.
            // We do NOT reload here, because WC is already clean and reloading would
            // interrupt Privy's embedded wallet creation flow for new users.
            await deleteWCIDB().catch(() => {});
          } else {
            // We restored a session, so we MUST reload for WC to read it from IDB.
            window.location.reload();
            return;
          }
        }
      }
    }

    // and we already initialized for this specific token, stop to avoid loops.
    if (accessToken && lastInitTokenRef.current === accessToken) {
      return;
    }

    isSyncingRef.current = true;
    try {
      refreshAttemptedRef.current = true;

      const cookieAccessToken = getAccessToken();
      const cookieRefreshToken = getPersistedTokenForRefresh();
      let currentToken = accessToken;

      // STEP 1: Restore Session
      // If we have an access token in cookies but not in Redux, sync it first
      if (!currentToken && cookieAccessToken) {
        dispatch(
          setTokens({
            accessToken: cookieAccessToken,
            refreshToken: cookieRefreshToken || '',
            provider: 'backend',
          }),
        );
        currentToken = cookieAccessToken;
      }

      // If we still don't have an access token but have a refresh token, perform refresh
      if (!currentToken && cookieRefreshToken) {
        try {
          const { accessToken: newToken } = await authApi.refreshToken(cookieRefreshToken);
          dispatch(
            setTokens({
              accessToken: newToken,
              refreshToken: cookieRefreshToken,
              provider: 'backend',
            }),
          );
          currentToken = newToken;
        } catch (err) {
          // If refresh fails (e.g., token expired/revoked), wipe the state
          dispatch(clearAuth());
          // Don't return here, maybe we have a Privy session instead?
        }
      }

      // New: Handle session expiry cleanup (Force Logout) migrated from AuthParamsHandler
      if (typeof window !== 'undefined' && localStorage.getItem('force_logout_marker') === 'true') {
        localStorage.removeItem('force_logout_marker');
        logoutMutation.mutate();
        dispatch(clearAuth());
        return;
      }

      // If no backend tokens, check for a valid Privy session (e.g. returning from OAuth redirect)
      if (!currentToken && ready && authenticated) {
        dispatch(clearError());
        try {
          const pToken = await getPrivyToken();
          if (pToken) {
            // Only pass a name to login if it's likely a real display name, not just an email
            const privyName = privyUser?.google?.name || '';
            const userName =
              privyName || privyUser?.apple?.email || privyUser?.email?.address || 'User';

            const referralCode = getPersistedReferralCode();
            const response = await authApi.login(pToken, userName, referralCode || undefined);
            if (response?.user && response?.accessToken) {
              // Clear referral code after successful claim
              if (referralCode) clearPersistedReferralCode();

              // Always use the backend's mapped user to ensure displayName priority
              dispatch(setUser(response.user));

              dispatch(
                setTokens({
                  accessToken: response.accessToken,
                  refreshToken: response.refreshToken,
                  provider: 'backend',
                }),
              );
              dispatch(setSetupRequested(true));
              currentToken = response.accessToken;

              // If Apple/Google forced a full top-level redirect (popups blocked
              // on mobile/in-app browsers), the page reloaded and dropped all
              // React/router state. Recover where the user was trying to go —
              // see oauthContext.ts / AuthModal's handleAppleSignIn/handleGoogleSignIn.
              const oauthCtx = getOAuthContext();
              if (oauthCtx) {
                clearOAuthContext();
                if (oauthCtx.returnTo !== window.location.pathname) {
                  router.replace(oauthCtx.returnTo);
                }
              }
            }
          }
        } catch (err: any) {
          console.error('[AuthInitializer] Privy synchronization failed:', err);
          logoutMutation.mutate();
          dispatch(setError(err?.message || 'Synchronization with backend failed'));
        }
      }

      // If no tokens found at all, stop loading
      if (!currentToken && !cookieRefreshToken) {
        dispatch(setLoading(false));
        return;
      }

      // STEP 2: Sync User Status
      // Once we have a valid token, fetch the latest user profile from the backend
      if (currentToken) {
        try {
          const status = await authApi.getStatus();
          if (status.authenticated && status.user) {
            // Explicitly map the user profile to ensure displayName priority and clean identity
            const mappedUser = mapBackendUserToUser(status.user);
            dispatch(setUser(mappedUser));

            if (!mappedUser.walletAddress && ready && authenticated && privyUser?.wallet?.address) {
              try {
                // Use whichever connected wallet we selected for this session.
                // This keeps existing embedded behavior while allowing external-wallet sessions.
                const walletAddress = selectedWalletAddress;
                await walletApi.linkWallet({
                  privyUserId: privyUser.id,
                  eoaAddress: walletAddress,
                  walletType: selectedWalletType,
                });

                // Force update Redux state immediately so downstream components (like ProxyInitializer) see it
                dispatch(updateUser({ walletAddress }));
              } catch (linkErr) {
                console.error('[AuthInitializer] Background wallet link failed:', linkErr);
              }
            }
            dispatch(setSetupRequested(true));

            // Sync Polymarket proxy + CLOB wallet kind (404 ⇒ new user ⇒ deposit-wallet flow)
            try {
              const pr = await walletApi.getProxyInfo();
              if (pr?.success && pr.data?.proxyAddress) {
                dispatch(
                  updateUser({
                    proxyWalletAddress: pr.data.proxyAddress,
                    tradingWalletKind: pr.data.tradingWalletKind ?? 'gnosis_safe',
                  }),
                );
              } else if (pr?.success === true && !pr.data?.proxyAddress) {
                dispatch(updateUser({ tradingWalletKind: 'deposit_wallet' }));
              }
            } catch (err: any) {
              if (err?.response?.status === 404) {
                dispatch(updateUser({ tradingWalletKind: 'deposit_wallet' }));
              }
            }
          } else if (!status.authenticated) {
            // Explicitly signed out on the backend server
            dispatch(clearAuth());
          }

          // Mark this token as initialized
          lastInitTokenRef.current = currentToken;
        } catch (err) {
          // Protect against transient network errors: only wipe state if we don't already have user data.
          // If we have a user (e.g., from a fresh login), we keep the session alive even if getStatus fails once.
          if (!store.getState().auth.user) dispatch(clearAuth());
        } finally {
          // Mark initialization as finished regardless of profile sync success
          dispatch(setLoading(false));
        }
      }
    } finally {
      isSyncingRef.current = false;
    }
  };

  useEffect(() => {
    initAuth();
  }, [accessToken, ready, authenticated, privyUser?.id, dispatch]);

  // With headless Privy hooks, `createOnLogin` does not fire automatically.
  // Explicitly create the embedded wallet when the user is authenticated but
  // has no embedded wallet yet (one wallet per user). Failed attempts are
  // retried with backoff (see MAX_WALLET_CREATE_ATTEMPTS above) — the first
  // attempt often races the wallet iframe load right after a headless login.
  useEffect(() => {
    const currentUserId = privyUser?.id ?? null;
    if (walletCreationRef.current.userId !== currentUserId) {
      walletCreationRef.current = {
        userId: currentUserId,
        attempts: 0,
        inFlight: false,
        done: false,
      };
    }

    if (!ready || !authenticated || !privyUser || embeddedWallet) return;

    const state = walletCreationRef.current;
    if (state.done || state.inFlight || state.attempts >= MAX_WALLET_CREATE_ATTEMPTS) return;

    state.attempts += 1;
    state.inFlight = true;
    // usePrivy().createWallet creates the primary Ethereum embedded wallet.
    // (It takes no `chainType` — that option belongs to the extended-chains API.)
    createWallet()
      .then(() => {
        state.done = true;
      })
      .catch((err) => {
        const msg = String(err?.message || err || '');
        // Wallet already exists server-side — it will surface through
        // useWallets() once the iframe syncs. Nothing left to create.
        if (/already has/i.test(msg)) {
          state.done = true;
          return;
        }
        console.error(
          `[AuthInitializer] createWallet attempt ${state.attempts}/${MAX_WALLET_CREATE_ATTEMPTS} failed:`,
          msg,
        );
        if (state.attempts < MAX_WALLET_CREATE_ATTEMPTS) {
          setTimeout(
            () => setWalletCreateRetryTick((t) => t + 1),
            WALLET_CREATE_RETRY_BASE_MS * state.attempts,
          );
        } else {
          // Out of retries — surface it instead of failing silently, since the
          // rest of onboarding (linking, proxy deploy, deposits) depends on it.
          toast.error(MESSAGES.AUTH.WALLET_LINK_FAILED);
        }
      })
      .finally(() => {
        state.inFlight = false;
      });
  }, [ready, authenticated, privyUser, embeddedWallet, createWallet, walletCreateRetryTick]);

  // Once the embedded wallet appears (after createWallet resolves), link it to the backend
  // so that user.walletAddress is set and ProxyWalletInitializer can proceed.
  // initAuth only runs on its specific deps and can't catch a wallet that appears later.
  useEffect(() => {
    // Reset the one-shot guard when a different Privy user logs in in this tab.
    if (linkedNewWalletUserIdRef.current !== (privyUser?.id ?? null)) {
      linkedNewWalletUserIdRef.current = privyUser?.id ?? null;
      hasLinkedNewWalletRef.current = false;
    }

    if (
      !ready ||
      !authenticated ||
      !privyUser?.id ||
      !embeddedWallet?.address ||
      user?.walletAddress ||
      !accessToken ||
      hasLinkedNewWalletRef.current
    )
      return;

    hasLinkedNewWalletRef.current = true;
    walletApi
      .linkWallet({
        privyUserId: privyUser.id,
        eoaAddress: embeddedWallet.address,
        walletType: 'privy_embedded',
      })
      .then(() => {
        dispatch(updateUser({ walletAddress: embeddedWallet.address }));
        dispatch(setSetupRequested(true));
      })
      .catch((err) => {
        console.warn('[AuthInitializer] linkWallet for new wallet failed:', err?.message || err);
        // Allow retry on next render cycle
        hasLinkedNewWalletRef.current = false;
      });
  }, [
    ready,
    authenticated,
    privyUser?.id,
    embeddedWallet?.address,
    user?.walletAddress,
    accessToken,
    dispatch,
  ]);

  return null;
}

export default AuthInitializer;
